import { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import AsyncCreatableSelect from 'react-select/async-creatable';
import DynamicIcon from "../DynamicIcon";
import {
    createSupplier,
    updateSupplier,
    selectSupplierDetails,
    selectSupplierDetailsLoading,
    selectSupplierCreateLoading,
    selectSupplierCreateError,
    selectSupplierUpdateLoading,
    selectSupplierUpdateError
} from "../../redux/suppliers/suppliersSlice";
import { closeModal, setHasToRefresh } from "../../redux/rightModalSlice";
import { selectAccountData } from "../../redux/main/accountSlice";
import LoadingSection from "../LoadingSection";

export default function AddEditSupplier({ mode }) {
    const dispatch = useDispatch();
    const accountData = useSelector(selectAccountData);
    const token = localStorage.getItem('installamelo-auth-token');
    const supplierDetails = useSelector(selectSupplierDetails);
    const supplierDetailsLoading = useSelector(selectSupplierDetailsLoading);
    const createLoading = useSelector(selectSupplierCreateLoading);
    const createError = useSelector(selectSupplierCreateError);
    const updateLoading = useSelector(selectSupplierUpdateLoading);
    const updateError = useSelector(selectSupplierUpdateError);

    // Form state
    const [formData, setFormData] = useState({
        code: '',
        company: '',
        vatNumber: '',
        fiscalCode: '',
        sdiCode: '',
        pec: '',
        address: '',
        city: '',
        province: '',
        postalCode: '',
        state: '',
        phone: '',
        mobile: '',
        fax: '',
        email: '',
        bankName: '',
        iban: '',
        abi: '',
        cab: '',
        swift: '',
        paymentType: '',
        paymentLabel: '',
        vatId: '',
        vatValue: '',
        vatLabel: '',
        brands: [],
        listini: [],
        agente: '',
        agenteEmail: '',
        agentePhone: '',
        active: true
    });

    const [validation, setValidation] = useState({
        company: true
    });

    // Popola il form in modalita' modifica
    useEffect(() => {
        if (mode === 'edit' && supplierDetails?._id) {
            setFormData({
                code: supplierDetails.code || '',
                company: supplierDetails.company || '',
                vatNumber: supplierDetails.vatNumber || '',
                fiscalCode: supplierDetails.fiscalCode || '',
                sdiCode: supplierDetails.sdiCode || '',
                pec: supplierDetails.pec || '',
                address: supplierDetails.address || '',
                city: supplierDetails.city || '',
                province: supplierDetails.province || '',
                postalCode: supplierDetails.postalCode || '',
                state: supplierDetails.state || '',
                phone: supplierDetails.phone || '',
                mobile: supplierDetails.mobile || '',
                fax: supplierDetails.fax || '',
                email: supplierDetails.email || '',
                bankName: supplierDetails.bankName || '',
                iban: supplierDetails.iban || '',
                abi: supplierDetails.abi || '',
                cab: supplierDetails.cab || '',
                swift: supplierDetails.swift || '',
                paymentType: supplierDetails.paymentType || '',
                paymentLabel: supplierDetails.paymentLabel || '',
                vatId: supplierDetails.vatId || '',
                vatValue: supplierDetails.vatValue || '',
                vatLabel: supplierDetails.vatLabel || '',
                brands: supplierDetails.brands || [],
                listini: supplierDetails.listini || [],
                agente: supplierDetails.agente || '',
                agenteEmail: supplierDetails.agenteEmail || '',
                agentePhone: supplierDetails.agentePhone || '',
                active: supplierDetails.active !== undefined ? supplierDetails.active : true
            });
        }
    }, [mode, supplierDetails]);

    // Gestione cambio campi input
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Reset validazione per il campo modificato
        if (name in validation) {
            setValidation(prev => ({
                ...prev,
                [name]: true
            }));
        }
    };

    // --- Gestione Marchi (brands) con select searchable ---
    const loadBrandOptions = useCallback(async (inputValue) => {
        try {
            const url = `${import.meta.env.VITE_API_BASE_URL}/brands/getForSelect2?accountId=${accountData._id}&searchTerm=${encodeURIComponent(inputValue || '')}&page=0&pagination=20`;
            const res = await fetch(url, { headers: { 'auth-token': token } });
            const data = await res.json();
            return (data.results || []).map(b => ({
                value: b.id || b._id,
                label: b.name || b.title || b.text
            }));
        } catch {
            return [];
        }
    }, [accountData, token]);

    const handleBrandsChange = async (selectedOptions) => {
        const brands = [];
        for (const opt of (selectedOptions || [])) {
            if (opt.__isNew__) {
                // Crea il nuovo marchio nella collection Brand
                try {
                    const res = await fetch(`${import.meta.env.VITE_API_BASE_URL}/brands`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'auth-token': token },
                        body: JSON.stringify({ brand: { accountId: accountData._id, name: opt.label } })
                    });
                    const newBrandId = await res.json();
                    brands.push({ brandId: newBrandId, brandLabel: opt.label });
                } catch {
                    brands.push({ brandLabel: opt.label });
                }
            } else {
                brands.push({ brandId: opt.value, brandLabel: opt.label });
            }
        }
        setFormData(prev => ({ ...prev, brands }));
    };

    // Converte i brands del form in opzioni per react-select
    const selectedBrandOptions = formData.brands.map(b => ({
        value: b.brandId || b.brandLabel,
        label: b.brandLabel
    }));

    // --- Gestione Listini ---
    const handleAddListino = () => {
        setFormData(prev => ({
            ...prev,
            listini: [...prev.listini, { listinoId: '', listinoLabel: '', files: [] }]
        }));
    };

    const handleRemoveListino = (index) => {
        setFormData(prev => ({
            ...prev,
            listini: prev.listini.filter((_, i) => i !== index)
        }));
    };

    const handleListinoChange = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            listini: prev.listini.map((listino, i) =>
                i === index ? { ...listino, [field]: value } : listino
            )
        }));
    };

    // --- Gestione File Listino ---
    const fileInputRefs = useRef({});
    const [uploadingListinoIndex, setUploadingListinoIndex] = useState(null);

    const getFileIcon = (kind) => {
        if (!kind) return 'fas fa-file';
        if (kind.includes('pdf')) return 'fas fa-file-pdf';
        if (kind.includes('sheet') || kind.includes('excel') || kind.includes('csv')) return 'fas fa-file-excel';
        if (kind.includes('word') || kind.includes('document')) return 'fas fa-file-word';
        if (kind.includes('image')) return 'fas fa-file-image';
        return 'fas fa-file';
    };

    const getFileIconColor = (kind) => {
        if (!kind) return 'text-gray-500';
        if (kind.includes('pdf')) return 'text-red-500';
        if (kind.includes('sheet') || kind.includes('excel') || kind.includes('csv')) return 'text-green-600';
        if (kind.includes('word') || kind.includes('document')) return 'text-blue-600';
        if (kind.includes('image')) return 'text-purple-500';
        return 'text-gray-500';
    };

    const handleFileUpload = async (listinoIndex, file) => {
        if (!file) return;
        setUploadingListinoIndex(listinoIndex);
        try {
            const uploadFormData = new FormData();
            uploadFormData.append('file', file);
            uploadFormData.append('path', `suppliers/listini`);
            uploadFormData.append('uploadTitle', file.name);

            const res = await fetch(`${import.meta.env.VITE_API_BASE_URL}/files/upload`, {
                method: 'POST',
                headers: { 'auth-token': token },
                body: uploadFormData
            });

            if (!res.ok) throw new Error('Errore durante l\'upload');

            const data = await res.json();
            const newFile = {
                fileName: data.fileName,
                urlFile: data.urlFile,
                kind: data.kind,
                uploadDate: new Date()
            };

            setFormData(prev => ({
                ...prev,
                listini: prev.listini.map((listino, i) =>
                    i === listinoIndex
                        ? { ...listino, files: [...(listino.files || []), newFile] }
                        : listino
                )
            }));
        } catch (err) {
            console.error('Errore upload file:', err);
            alert('Errore durante il caricamento del file');
        } finally {
            setUploadingListinoIndex(null);
        }
    };

    const handleRemoveFile = (listinoIndex, fileIndex) => {
        setFormData(prev => ({
            ...prev,
            listini: prev.listini.map((listino, i) =>
                i === listinoIndex
                    ? { ...listino, files: (listino.files || []).filter((_, fi) => fi !== fileIndex) }
                    : listino
            )
        }));
    };

    // Validazione form
    const validateForm = () => {
        const newValidation = {
            company: !!formData.company.trim()
        };

        setValidation(newValidation);
        return Object.values(newValidation).every(isValid => isValid);
    };

    // Invio form
    const handleSubmit = (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        if (mode === 'add') {
            dispatch(createSupplier({
                supplier: {
                    ...formData,
                    accountId: accountData._id
                },
                token
            })).then((result) => {
                if (!result.error) {
                    dispatch(setHasToRefresh());
                    dispatch(closeModal());
                }
            });
        } else {
            dispatch(updateSupplier({
                supplier: {
                    ...formData,
                    _id: supplierDetails._id,
                    accountId: supplierDetails.accountId
                },
                token
            })).then((result) => {
                if (!result.error) {
                    dispatch(setHasToRefresh());
                    dispatch(closeModal());
                }
            });
        }
    };

    if (mode === 'edit' && supplierDetailsLoading) {
        return <LoadingSection />;
    }

    const inputClass = "w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500";
    const inputErrorClass = "w-full p-2 border border-red-500 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500";

    return (
        <div className="p-6">
            <h2 className="text-2xl font-bold mb-6">
                {mode === 'add' ? 'Nuovo Fornitore' : 'Modifica Fornitore'}
            </h2>

            {(createLoading || updateLoading) && <LoadingSection isFixed />}

            <form onSubmit={handleSubmit}>

                {/* === Sezione: Dati Aziendali === */}
                <h3 className="text-lg font-semibold mb-3 mt-6">Dati Aziendali</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
                            Ragione Sociale*
                        </label>
                        <input
                            type="text"
                            id="company"
                            name="company"
                            value={formData.company}
                            onChange={handleInputChange}
                            className={!validation.company ? inputErrorClass : inputClass}
                            placeholder="Ragione Sociale"
                        />
                        {!validation.company && (
                            <p className="text-red-500 text-sm mt-1">La ragione sociale è obbligatoria</p>
                        )}
                    </div>
                    <div>
                        <label htmlFor="vatNumber" className="block text-sm font-medium text-gray-700 mb-1">
                            Partita IVA
                        </label>
                        <input
                            type="text"
                            id="vatNumber"
                            name="vatNumber"
                            value={formData.vatNumber}
                            onChange={handleInputChange}
                            className={inputClass}
                            placeholder="Partita IVA"
                        />
                    </div>
                    <div>
                        <label htmlFor="fiscalCode" className="block text-sm font-medium text-gray-700 mb-1">
                            Codice Fiscale
                        </label>
                        <input
                            type="text"
                            id="fiscalCode"
                            name="fiscalCode"
                            value={formData.fiscalCode}
                            onChange={handleInputChange}
                            className={inputClass}
                            placeholder="Codice Fiscale"
                        />
                    </div>
                    <div>
                        <label htmlFor="sdiCode" className="block text-sm font-medium text-gray-700 mb-1">
                            Codice SDI
                        </label>
                        <input
                            type="text"
                            id="sdiCode"
                            name="sdiCode"
                            value={formData.sdiCode}
                            onChange={handleInputChange}
                            className={inputClass}
                            placeholder="Codice SDI"
                        />
                    </div>
                    <div>
                        <label htmlFor="pec" className="block text-sm font-medium text-gray-700 mb-1">
                            PEC
                        </label>
                        <input
                            type="email"
                            id="pec"
                            name="pec"
                            value={formData.pec}
                            onChange={handleInputChange}
                            className={inputClass}
                            placeholder="PEC"
                        />
                    </div>
                    <div>
                        <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1">
                            Codice
                        </label>
                        <input
                            type="text"
                            id="code"
                            name="code"
                            value={formData.code}
                            onChange={handleInputChange}
                            className={inputClass}
                            placeholder="Codice fornitore"
                        />
                    </div>
                </div>

                {/* === Sezione: Indirizzo === */}
                <h3 className="text-lg font-semibold mb-3 mt-6">Indirizzo</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                        <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
                            Indirizzo
                        </label>
                        <input
                            type="text"
                            id="address"
                            name="address"
                            value={formData.address}
                            onChange={handleInputChange}
                            className={inputClass}
                            placeholder="Via / Piazza"
                        />
                    </div>
                    <div>
                        <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                            Città
                        </label>
                        <input
                            type="text"
                            id="city"
                            name="city"
                            value={formData.city}
                            onChange={handleInputChange}
                            className={inputClass}
                            placeholder="Città"
                        />
                    </div>
                    <div>
                        <label htmlFor="province" className="block text-sm font-medium text-gray-700 mb-1">
                            Provincia
                        </label>
                        <input
                            type="text"
                            id="province"
                            name="province"
                            value={formData.province}
                            onChange={handleInputChange}
                            className={inputClass}
                            placeholder="Provincia"
                        />
                    </div>
                    <div>
                        <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700 mb-1">
                            CAP
                        </label>
                        <input
                            type="text"
                            id="postalCode"
                            name="postalCode"
                            value={formData.postalCode}
                            onChange={handleInputChange}
                            className={inputClass}
                            placeholder="CAP"
                        />
                    </div>
                    <div>
                        <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-1">
                            Nazione
                        </label>
                        <input
                            type="text"
                            id="state"
                            name="state"
                            value={formData.state}
                            onChange={handleInputChange}
                            className={inputClass}
                            placeholder="Nazione"
                        />
                    </div>
                </div>

                {/* === Sezione: Contatti === */}
                <h3 className="text-lg font-semibold mb-3 mt-6">Contatti</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                            Telefono
                        </label>
                        <input
                            type="text"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            className={inputClass}
                            placeholder="Telefono"
                        />
                    </div>
                    <div>
                        <label htmlFor="mobile" className="block text-sm font-medium text-gray-700 mb-1">
                            Cellulare
                        </label>
                        <input
                            type="text"
                            id="mobile"
                            name="mobile"
                            value={formData.mobile}
                            onChange={handleInputChange}
                            className={inputClass}
                            placeholder="Cellulare"
                        />
                    </div>
                    <div>
                        <label htmlFor="fax" className="block text-sm font-medium text-gray-700 mb-1">
                            Fax
                        </label>
                        <input
                            type="text"
                            id="fax"
                            name="fax"
                            value={formData.fax}
                            onChange={handleInputChange}
                            className={inputClass}
                            placeholder="Fax"
                        />
                    </div>
                    <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                            Email
                        </label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            className={inputClass}
                            placeholder="Email"
                        />
                    </div>
                </div>

                {/* === Sezione: Dati Bancari === */}
                <h3 className="text-lg font-semibold mb-3 mt-6">Dati Bancari</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                        <label htmlFor="bankName" className="block text-sm font-medium text-gray-700 mb-1">
                            Nome Banca
                        </label>
                        <input
                            type="text"
                            id="bankName"
                            name="bankName"
                            value={formData.bankName}
                            onChange={handleInputChange}
                            className={inputClass}
                            placeholder="Nome Banca"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label htmlFor="iban" className="block text-sm font-medium text-gray-700 mb-1">
                            IBAN
                        </label>
                        <input
                            type="text"
                            id="iban"
                            name="iban"
                            value={formData.iban}
                            onChange={handleInputChange}
                            className={inputClass}
                            placeholder="IBAN"
                        />
                    </div>
                    <div>
                        <label htmlFor="abi" className="block text-sm font-medium text-gray-700 mb-1">
                            ABI
                        </label>
                        <input
                            type="text"
                            id="abi"
                            name="abi"
                            value={formData.abi}
                            onChange={handleInputChange}
                            className={inputClass}
                            placeholder="ABI"
                        />
                    </div>
                    <div>
                        <label htmlFor="cab" className="block text-sm font-medium text-gray-700 mb-1">
                            CAB
                        </label>
                        <input
                            type="text"
                            id="cab"
                            name="cab"
                            value={formData.cab}
                            onChange={handleInputChange}
                            className={inputClass}
                            placeholder="CAB"
                        />
                    </div>
                    <div>
                        <label htmlFor="swift" className="block text-sm font-medium text-gray-700 mb-1">
                            SWIFT / BIC
                        </label>
                        <input
                            type="text"
                            id="swift"
                            name="swift"
                            value={formData.swift}
                            onChange={handleInputChange}
                            className={inputClass}
                            placeholder="SWIFT / BIC"
                        />
                    </div>
                </div>

                {/* === Sezione: Condizioni Pagamento === */}
                <h3 className="text-lg font-semibold mb-3 mt-6">Condizioni Pagamento</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label htmlFor="paymentType" className="block text-sm font-medium text-gray-700 mb-1">
                            Tipo Pagamento
                        </label>
                        <input
                            type="text"
                            id="paymentType"
                            name="paymentType"
                            value={formData.paymentType}
                            onChange={handleInputChange}
                            className={inputClass}
                            placeholder="Tipo Pagamento"
                        />
                    </div>
                    <div>
                        <label htmlFor="vatValue" className="block text-sm font-medium text-gray-700 mb-1">
                            Aliquota IVA
                        </label>
                        <input
                            type="text"
                            id="vatValue"
                            name="vatValue"
                            value={formData.vatValue}
                            onChange={handleInputChange}
                            className={inputClass}
                            placeholder="Aliquota IVA"
                        />
                    </div>
                    <div>
                        <label htmlFor="vatLabel" className="block text-sm font-medium text-gray-700 mb-1">
                            Descrizione IVA
                        </label>
                        <input
                            type="text"
                            id="vatLabel"
                            name="vatLabel"
                            value={formData.vatLabel}
                            onChange={handleInputChange}
                            className={inputClass}
                            placeholder="Descrizione IVA"
                        />
                    </div>
                </div>

                {/* === Sezione: Marchi Trattati === */}
                <h3 className="text-lg font-semibold mb-3 mt-6">Marchi Trattati</h3>
                <AsyncCreatableSelect
                    isMulti
                    cacheOptions
                    defaultOptions
                    loadOptions={loadBrandOptions}
                    value={selectedBrandOptions}
                    onChange={handleBrandsChange}
                    placeholder="Cerca o crea un marchio..."
                    noOptionsMessage={() => "Nessun marchio trovato"}
                    formatCreateLabel={(inputValue) => `Crea "${inputValue}"`}
                    styles={{
                        control: (base) => ({ ...base, borderColor: '#d1d5db', borderRadius: '0.375rem', minHeight: '42px' }),
                        multiValue: (base) => ({ ...base, backgroundColor: '#dbeafe', borderRadius: '9999px' }),
                        multiValueLabel: (base) => ({ ...base, color: '#1e40af', fontWeight: 500 }),
                        multiValueRemove: (base) => ({ ...base, color: '#1e40af', ':hover': { backgroundColor: '#bfdbfe', color: '#1e3a8a' } })
                    }}
                />

                {/* === Sezione: Listini === */}
                <h3 className="text-lg font-semibold mb-3 mt-6">Listini</h3>
                {formData.listini.map((listino, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-3 mb-3">
                        <div className="flex items-center gap-2 mb-2">
                            <input
                                type="text"
                                value={listino.listinoId}
                                onChange={(e) => handleListinoChange(index, 'listinoId', e.target.value)}
                                className={inputClass}
                                placeholder="ID Listino"
                            />
                            <input
                                type="text"
                                value={listino.listinoLabel}
                                onChange={(e) => handleListinoChange(index, 'listinoLabel', e.target.value)}
                                className={inputClass}
                                placeholder="Nome Listino"
                            />
                            <button
                                type="button"
                                className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md"
                                onClick={() => handleRemoveListino(index)}
                                title="Rimuovi listino"
                            >
                                <DynamicIcon iconClass="fas fa-trash" />
                            </button>
                        </div>

                        {/* File del listino */}
                        {(listino.files || []).length > 0 && (
                            <div className="ml-1 mb-2">
                                {listino.files.map((file, fileIndex) => (
                                    <div key={fileIndex} className="flex items-center gap-2 py-1 px-2 bg-gray-50 rounded mb-1">
                                        <DynamicIcon iconClass={getFileIcon(file.kind)} className={getFileIconColor(file.kind)} />
                                        <a
                                            href={file.urlFile}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-sm text-blue-600 hover:underline truncate flex-1"
                                            title={file.fileName}
                                        >
                                            {file.fileName}
                                        </a>
                                        <button
                                            type="button"
                                            className="p-1 text-red-400 hover:text-red-600"
                                            onClick={() => handleRemoveFile(index, fileIndex)}
                                            title="Rimuovi file"
                                        >
                                            <DynamicIcon iconClass="fas fa-times" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Upload file */}
                        <input
                            type="file"
                            ref={(el) => fileInputRefs.current[index] = el}
                            className="hidden"
                            onChange={(e) => {
                                handleFileUpload(index, e.target.files[0]);
                                e.target.value = '';
                            }}
                        />
                        <button
                            type="button"
                            className="mt-1 px-2 py-1 text-xs text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded border border-gray-300"
                            onClick={() => fileInputRefs.current[index]?.click()}
                            disabled={uploadingListinoIndex === index}
                        >
                            {uploadingListinoIndex === index ? (
                                <><DynamicIcon iconClass="fas fa-spinner fa-spin" /> Caricamento...</>
                            ) : (
                                <><DynamicIcon iconClass="fas fa-upload" /> Carica file</>
                            )}
                        </button>
                    </div>
                ))}
                <button
                    type="button"
                    className="mt-1 px-3 py-2 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md border border-blue-300"
                    onClick={handleAddListino}
                >
                    <DynamicIcon iconClass="fas fa-plus" /> Aggiungi Listino
                </button>

                {/* === Sezione: Agente / Referente === */}
                <h3 className="text-lg font-semibold mb-3 mt-6">Agente / Referente</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                        <label htmlFor="agente" className="block text-sm font-medium text-gray-700 mb-1">
                            Nome Agente
                        </label>
                        <input
                            type="text"
                            id="agente"
                            name="agente"
                            value={formData.agente}
                            onChange={handleInputChange}
                            className={inputClass}
                            placeholder="Nome Agente"
                        />
                    </div>
                    <div>
                        <label htmlFor="agenteEmail" className="block text-sm font-medium text-gray-700 mb-1">
                            Email Agente
                        </label>
                        <input
                            type="email"
                            id="agenteEmail"
                            name="agenteEmail"
                            value={formData.agenteEmail}
                            onChange={handleInputChange}
                            className={inputClass}
                            placeholder="Email Agente"
                        />
                    </div>
                    <div>
                        <label htmlFor="agentePhone" className="block text-sm font-medium text-gray-700 mb-1">
                            Telefono Agente
                        </label>
                        <input
                            type="text"
                            id="agentePhone"
                            name="agentePhone"
                            value={formData.agentePhone}
                            onChange={handleInputChange}
                            className={inputClass}
                            placeholder="Telefono Agente"
                        />
                    </div>
                </div>

                {/* Messaggio di errore */}
                {(createError || updateError) && (
                    <div className="mb-4 mt-4 p-2 bg-red-100 text-red-700 rounded-md">
                        <p>{createError || updateError}</p>
                    </div>
                )}

                {/* Bottoni azioni */}
                <div className="flex justify-end space-x-3 mt-8">
                    <button
                        type="button"
                        className="secondary"
                        onClick={() => dispatch(closeModal())}
                        disabled={createLoading || updateLoading}
                    >
                        Annulla
                    </button>

                    <button
                        type="submit"
                        className="primary"
                        disabled={createLoading || updateLoading}
                    >
                        {createLoading || updateLoading ? (
                            <>
                                <DynamicIcon iconClass="fas fa-spinner fa-spin" />
                                Salvataggio...
                            </>
                        ) : (
                            <>
                                <DynamicIcon iconClass="fas fa-save" />
                                Salva
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
}
